import { useRef, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Award, Upload, Trash2, FileText, Plus } from "lucide-react";
import DateInput from "@/components/ui/date-input";
import SertifikatBerlakuBadge from "@/components/workers/SertifikatBerlakuBadge";
import { SERTIFIKAT_PREFIX, isSertifikat, labelSertifikat } from "@/lib/sertifikat";
export { SERTIFIKAT_PREFIX, isSertifikat, labelSertifikat };

const OK_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

// Checklist "Memiliki Sertifikasi" + unggah banyak sertifikat (file + keterangan) di form pekerja.
export default function SertifikasiSection({ checked, onChecked, existing = [], removeIds = [], onRemoveExisting, pending = [], onPendingChange, onError, berlakuEdit = {}, onBerlakuEdit }) {
  const fileRef = useRef(null);
  const [ket, setKet] = useState("");
  const [berlaku, setBerlaku] = useState("");
  const [file, setFile] = useState(null);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    if (!OK_TYPES.includes(f.type)) { onError("Sertifikat harus berupa PDF, JPG, PNG, atau WEBP."); return; }
    if (f.size > 10 * 1024 * 1024) { onError("Ukuran sertifikat maksimal 10 MB."); return; }
    onError(""); setFile(f);
  };
  const tambah = () => {
    if (!file) { onError("Pilih file sertifikat terlebih dahulu."); return; }
    if (!ket.trim()) { onError("Isi keterangan sertifikat (mis. Sertifikat K3 Umum)."); return; }
    onPendingChange([...pending, { file, keterangan: ket.trim(), berlaku_sampai: berlaku }]); setFile(null); setKet(""); setBerlaku(""); onError("");
  };
  const aktif = existing.filter((d) => !removeIds.includes(d.id));

  return (
    <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="sertifikasi-section">
      <label className="flex items-center gap-2 cursor-pointer">
        <Checkbox checked={checked} onCheckedChange={(v) => onChecked(!!v)} data-testid="field-punya-sertifikasi" />
        <span className="text-sm font-semibold flex items-center gap-1.5"><Award className="h-4 w-4 text-amber-600" />Memiliki Sertifikasi</span>
        {aktif.length + pending.length > 0 && <span className="ml-1 rounded-full bg-amber-100 text-amber-800 px-2 text-[10px] font-bold">{aktif.length + pending.length}</span>}
      </label>
      {checked && (
        <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/40 p-3">
          {(aktif.length > 0 || pending.length > 0) && (
            <ul className="space-y-1.5" data-testid="sertifikat-list">
              {aktif.map((d) => { const dd = { ...d, berlaku_sampai: berlakuEdit[d.id] ?? d.berlaku_sampai }; return (
                <li key={d.id} className="flex flex-wrap items-center gap-2 text-xs bg-white rounded-md border border-slate-200 px-2.5 py-1.5" data-testid={`sertifikat-item-${d.id}`}>
                  <FileText className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                  <span className="font-semibold text-slate-800 truncate">{labelSertifikat(d)}</span>
                  <span className="text-slate-400 truncate">· {d.nama_file}</span>
                  <SertifikatBerlakuBadge dok={dd} />
                  <span className="ml-auto flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-500">Berlaku s/d</span>
                    <DateInput value={dd.berlaku_sampai || ""} onChange={(v) => onBerlakuEdit(d.id, v)} className="h-7 w-36 text-xs" data-testid={`sertifikat-berlaku-input-${d.id}`} />
                    <button type="button" onClick={() => onRemoveExisting(d.id)} className="text-rose-500 hover:text-rose-700" title="Hapus sertifikat" data-testid={`sertifikat-remove-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </span>
                </li>); })}
              {pending.map((p, i) => (
                <li key={`${p.keterangan}-${p.file.name}-${p.file.size}`} className="flex flex-wrap items-center gap-2 text-xs bg-white rounded-md border border-dashed border-amber-300 px-2.5 py-1.5" data-testid={`sertifikat-pending-${i}`}>
                  <Upload className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                  <span className="font-semibold text-slate-800 truncate">{p.keterangan}</span>
                  <span className="text-slate-400 truncate">· {p.file.name}</span>
                  <SertifikatBerlakuBadge dok={{ id: `p${i}`, berlaku_sampai: p.berlaku_sampai }} />
                  <span className="rounded bg-amber-100 text-amber-800 px-1.5 text-[10px]">baru</span>
                  <button type="button" onClick={() => onPendingChange(pending.filter((_, j) => j !== i))} className="ml-auto text-rose-500 hover:text-rose-700" data-testid={`sertifikat-pending-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr_auto_auto] gap-2 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Dokumen</Label>
              <input type="file" ref={fileRef} accept={OK_TYPES.join(",")} onChange={pickFile} className="hidden" data-testid="sertifikat-file-input" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="sertifikat-pick-button"
                className="gap-2 border-amber-300 text-amber-800 hover:bg-amber-100 h-10 w-full sm:w-auto">
                <Upload className="h-4 w-4" />{file ? <span className="truncate max-w-[140px]">{file.name}</span> : "Upload Dokumen"}
              </Button>
            </div>
            <div className="space-y-1">
              <Label htmlFor="sertifikat-ket" className="text-xs">Keterangan sertifikat yang dimiliki</Label>
              <Input id="sertifikat-ket" value={ket} onChange={(e) => setKet(e.target.value)} placeholder="Contoh: Sertifikat K3 Umum, Ahli Listrik, TKPK 1" className="h-10" data-testid="sertifikat-keterangan" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Berlaku sampai</Label>
              <DateInput value={berlaku} onChange={setBerlaku} className="h-10 w-full sm:w-40" data-testid="sertifikat-berlaku" />
            </div>
            <Button type="button" onClick={tambah} className="h-10 gap-1.5 bg-amber-600 hover:bg-amber-700 text-white" data-testid="sertifikat-add-button"><Plus className="h-4 w-4" />Tambah</Button>
          </div>
          <p className="text-xs text-slate-400">Bisa lebih dari satu sertifikat. PDF, JPG, PNG, atau WEBP, maks 10 MB per file. Sertifikat yang habis dalam ≤ 90 hari ditandai merah.</p>
        </div>
      )}
    </div>
  );
}
